export type InterceptPropertyChangeCallback<T> = (value: T, ...args: Array<any>) => void;

/**
 * Hook into changes of given property of given element.
 *
 * @param target Element to watch for property changes on
 * @param property Property to observe
 * @param callbackFn Function to call when the property changes
 */
export const interceptPropertyChange = <T extends HTMLElement, K extends keyof T>(
    target: T,
    property: K,
    callbackFn: InterceptPropertyChangeCallback<T[K]>
): void => {
    const prototype = Object.getPrototypeOf(target);
    const descriptor = Object.getOwnPropertyDescriptor(prototype, property);
    const originalSetter = descriptor.set;
    const originalGetter = descriptor.get;

    Object.defineProperty(target, property, {
        get: function (...args) {
            return originalGetter.apply(target, args);
        },
        set: function (value: T[K], ...args): void {
            // apply property value before calling custom callback
            originalSetter.apply(target, [value, ...args]);
            callbackFn(value, args);
        },
    });
};

/**
 * Observe property of the given source element and keep its value synced on the target element.
 *
 * @param source element to observe the property on
 * @param target element to copy the property to
 * @param property property to keep synced
 * @returns nothing
 */
export const syncProperty = <T extends HTMLElement, K extends keyof T & string>(
    source: T,
    target: HTMLElement,
    property: K
): void =>
    interceptPropertyChange(source, property, value => {
        if (value) {
            target.setAttribute(property, value as unknown as string);
        } else {
            target.removeAttribute(property);
        }
    });

/**
 * One-time copy of the source's property to the target.
 *
 * @param source element of which to copy the property
 * @param target element to copy the property to
 * @param property property to copy
 */
export const copyProperty = <T extends HTMLElement, K extends keyof T & string>(
    source: T,
    target: HTMLElement,
    property: K
): void => {
    const value = source[property] || source.getAttribute(property);

    if (value) {
        target.setAttribute(property, value as string);
    } else {
        target.removeAttribute(property);
    }
};
